import type { Order, PrintAck, PrintClaim } from '../types'
import { useFirestore } from './firebase'
import * as firestoreOrders from './firestore/orders'
import { isLivePrintClaim } from './printClaim'

const STORAGE_KEY = 'orders'
const DISPLAY_NUMBER_KEY = 'orderDisplayCounter'

function getOrdersLocal(): Order[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw) as Order[]
  } catch {
    return []
  }
}

function saveOrdersLocal(orders: Order[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(orders))
}

export function getOrders(): Order[] {
  if (useFirestore()) {
    // Only use place-scoped Firestore cache; never fall back to global localStorage
    // to avoid leaking another account/place's orders.
    return firestoreOrders.getOrders()
  }
  return getOrdersLocal()
}

export function saveOrders(orders: Order[]): void {
  if (useFirestore()) {
    firestoreOrders.saveOrders(orders)
    return
  }
  saveOrdersLocal(orders)
}

export async function getNextDisplayNumber(): Promise<string> {
  if (useFirestore()) return firestoreOrders.getNextDisplayNumber()
  const current = localStorage.getItem(DISPLAY_NUMBER_KEY)
  const next = current ? parseInt(current, 10) + 1 : 1
  localStorage.setItem(DISPLAY_NUMBER_KEY, String(next))
  return String(next).padStart(3, '0')
}

export function addOrder(order: Order): Order[] {
  if (useFirestore()) return firestoreOrders.addOrder(order)
  const orders = getOrdersLocal()
  const updated = [...orders, order]
  saveOrdersLocal(updated)
  return updated
}

export function updateOrder(id: string, patch: Partial<Order>): Order[] {
  if (useFirestore()) return firestoreOrders.updateOrder(id, patch)
  const orders = getOrdersLocal()
  const updated = orders.map((o) => (o.id === id ? { ...o, ...patch } : o))
  saveOrdersLocal(updated)
  return updated
}

export function setOrderPrintAck(orderId: string, stationId: string, ack: PrintAck): Order[] {
  if (useFirestore()) return firestoreOrders.setOrderPrintAck(orderId, stationId, ack)
  const orders = getOrdersLocal()
  const updated = orders.map((o) => {
    if (o.id !== orderId) return o
    return { ...o, printAcks: { ...o.printAcks, [stationId]: ack } }
  })
  saveOrdersLocal(updated)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('wortelogy-orders-updated'))
  }
  return updated
}

export async function claimOrderPrintSlot(
  orderId: string,
  slotKey: string,
  claim: PrintClaim,
  serverNowMs?: number,
): Promise<boolean> {
  if (useFirestore()) {
    return firestoreOrders.claimOrderPrintSlot(orderId, slotKey, claim, serverNowMs)
  }
  const orders = getOrdersLocal()
  const order = orders.find((o) => o.id === orderId)
  if (isLivePrintClaim(order?.printClaims?.[slotKey], serverNowMs ?? Date.now())) return false
  const updated = orders.map((o) => {
    if (o.id !== orderId) return o
    return { ...o, printClaims: { ...o.printClaims, [slotKey]: claim } }
  })
  saveOrdersLocal(updated)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('wortelogy-orders-updated'))
  }
  return true
}

export async function clearOrderPrintClaim(orderId: string, slotKey: string): Promise<void> {
  if (useFirestore()) {
    await firestoreOrders.clearOrderPrintClaim(orderId, slotKey)
    return
  }
  const orders = getOrdersLocal()
  const updated = orders.map((o) => {
    if (o.id !== orderId) return o
    const printClaims = { ...o.printClaims }
    delete printClaims[slotKey]
    return { ...o, printClaims }
  })
  saveOrdersLocal(updated)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('wortelogy-orders-updated'))
  }
}

/** Persist one order row; Firestore uses updateOrder so pending patches survive snapshots. */
export function persistOrderRow(order: Order): void {
  if (useFirestore()) {
    firestoreOrders.updateOrder(order.id, {
      items: order.items,
      stationProcessing: order.stationProcessing,
    })
    return
  }
  const orders = getOrdersLocal().map((o) => (o.id === order.id ? order : o))
  saveOrdersLocal(orders)
}

export function deleteOrder(id: string): Order[] {
  if (useFirestore()) return firestoreOrders.deleteOrder(id)
  const orders = getOrdersLocal().filter((o) => o.id !== id)
  saveOrdersLocal(orders)
  return orders
}

export async function primeOrdersCacheIfNeeded(): Promise<void> {
  if (useFirestore() && !firestoreOrders.isOrdersPrimed()) {
    await firestoreOrders.primeOrdersCache()
  }
}

/** Force re-fetch orders from Firestore (rebinds snapshot). No-op offline/local. */
export async function refreshOrdersFromServer(): Promise<void> {
  if (useFirestore()) {
    await firestoreOrders.primeOrdersCache()
  }
}
